import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/Layout/AdminLayout';
import { monitorApi } from '../../services/api';
import { formatDate } from '../../config/appConfig';
import { AuditLog } from '../../types';

const actionColor = (action: string): string => {
  if (action.includes('delete')) return 'bg-red-100 text-red-700';
  if (action.includes('create')) return 'bg-green-100 text-green-700';
  if (action.includes('update')) return 'bg-blue-100 text-blue-700';
  if (action.includes('login')) return 'bg-slate-100 text-slate-600';
  return 'bg-slate-100 text-slate-600';
};

const AdminAuditPage: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState<string>('all');
  const [userFilter, setUserFilter] = useState<string>('all');

  useEffect(() => {
    monitorApi.getAuditLogs()
      .then(res => setLogs(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const actions = Array.from(new Set(logs.map(l => l.action))).sort((a, b) => a.localeCompare(b));
  const users = Array.from(new Set(logs.map(l => l.performed_by))).sort((a, b) => a.localeCompare(b));

  const filteredLogs = logs.filter(l => {
    const actionMatches = actionFilter === 'all' ? true : l.action === actionFilter;
    const userMatches = userFilter === 'all' ? true : l.performed_by === userFilter;
    return actionMatches && userMatches;
  });

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto p-8">
        <h1 className="text-2xl font-bold text-slate-800 mb-1">Audit Log</h1>
        <p className="text-sm text-slate-500 mb-6">Recent administrative actions.</p>

        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Action</label>
              <select
                value={actionFilter}
                onChange={e => setActionFilter(e.target.value)}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="all">All actions</option>
                {actions.map(action => <option key={action} value={action}>{action}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">User</label>
              <select
                value={userFilter}
                onChange={e => setUserFilter(e.target.value)}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="all">All users</option>
                {users.map(user => <option key={user} value={user}>{user}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="px-5 py-16 text-center text-slate-400">Loading…</div>
          ) : logs.length === 0 ? (
            <div className="px-5 py-16 text-center text-slate-400">No audit entries.</div>
          ) : filteredLogs.length === 0 ? (
            <div className="px-5 py-16 text-center text-slate-400">No audit entries match the selected filters.</div>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full min-w-[980px] text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs text-slate-500 uppercase">
                  <th className="px-5 py-3 font-medium">Action</th>
                  <th className="px-5 py-3 font-medium">Entity</th>
                  <th className="px-5 py-3 font-medium">Details</th>
                  <th className="px-5 py-3 font-medium">By</th>
                  <th className="px-5 py-3 font-medium w-[220px]">When</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredLogs.map(l => (
                  <tr key={l.id} className="hover:bg-slate-50">
                    <td className="px-5 py-3"><span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${actionColor(l.action)}`}>{l.action}</span></td>
                    <td className="px-5 py-3 text-slate-600">{l.entity_type} #{l.entity_id}</td>
                    <td className="px-5 py-3 text-slate-600 max-w-md truncate">{l.details}</td>
                    <td className="px-5 py-3 text-slate-500">{l.performed_by}</td>
                    <td className="px-5 py-3 text-slate-400 whitespace-nowrap">{formatDate(l.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminAuditPage;
